import { useState } from "react";
import { Button } from "../../../components/Button/Button.tsx";
import { Dialog } from "../../../components/Dialog/Dialog.tsx";
import { POPULAR_TAG } from "../../Home/HomeIndex/Model/constants.ts";
import type { ModelType, ModelWithIsCompatibleAndTags } from "../../types.ts";
import { MODEL_TYPE_LABELS_WITH_ARTICLE } from "../display.ts";
import { ModelDescription } from "./components/ModelDescription.tsx";
import { ModelPrimaryNavigation } from "./components/ModelPrimaryNavigation.tsx";
import { ModelSecondaryNavigation } from "./components/ModelSecondaryNavigation.tsx";
import type { ModelPickerNavigationFilter } from "./types.ts";

export const ModelPickerDialog = ({
  models,
  dialogOpen,
  onDialogChange,
  onModelSelect,
  initialSelectedModelUuid,
  modelType,
}: {
  models: ModelWithIsCompatibleAndTags[];
  dialogOpen: boolean;
  onDialogChange: (dialogChange: boolean) => void;
  onModelSelect?: (model: ModelWithIsCompatibleAndTags) => void;
  initialSelectedModelUuid?: string;
  modelType: ModelType;
}) => (
  <Dialog
    title={`Select ${MODEL_TYPE_LABELS_WITH_ARTICLE[modelType]}`}
    isOpen={dialogOpen}
    onOpenChange={(dialogChange) => {
      onDialogChange(dialogChange);
    }}
    content={
      <ModelPickerContent
        models={models}
        onClose={() => onDialogChange(false)}
        onModelSubmit={onModelSelect}
        initialSelectedModelUuid={initialSelectedModelUuid}
        modelType={modelType}
      />
    }
  />
);

const ModelPickerContent = ({
  models,
  onModelSubmit,
  initialSelectedModelUuid,
  onClose,
  modelType,
}: {
  models: ModelWithIsCompatibleAndTags[];
  onModelSubmit?: (model: ModelWithIsCompatibleAndTags) => void;
  initialSelectedModelUuid?: string;
  onClose: () => void;
  modelType: ModelType;
}) => {
  // FIXME: We should defined the following states as atom to improve performance of model picker
  const [selectedModelUuid, setSelectedModelUuid] = useState<
    string | undefined
  >(initialSelectedModelUuid);
  const [modelPickerNavigationFilters, setModelPickerNavigationFilters] =
    useState<ModelPickerNavigationFilter>({
      selectedTag: POPULAR_TAG,
      selectedModelType: null,
      searchText: "",
      isTextSearchEnabled: true,
    });

  const selectedModel = models.find((it) => it.uuid === selectedModelUuid);

  return (
    // FIXME: move the Dialog sizing logic to the Dialog component
    <div className="max-w-[calc(100vw-24px)] max-h-[calc(100vh-76px)] h-[680px] flex-col text-primary">
      <div className="flex-fill flex-row border-t">
        <ModelPrimaryNavigation
          models={models}
          modelPickerNavigationFilters={modelPickerNavigationFilters}
          setModelPickerNavigationFilters={setModelPickerNavigationFilters}
          modelType={modelType}
        />
        <ModelSecondaryNavigation
          models={models}
          modelPickerNavigationFilters={modelPickerNavigationFilters}
          onModelUuidSelect={setSelectedModelUuid}
          selectedModelUuid={selectedModelUuid}
          initialSelectedModelUuid={initialSelectedModelUuid}
          modelType={modelType}
        />
        <div className="flex-col flex-fill w-[480px] justify-end">
          {selectedModel && <ModelDescription model={selectedModel} />}
          <div className="flex-row w-full py-300 px-400 gap-200 items-center justify-end border-t">
            <Button onClick={onClose} size="md" variant="tertiary">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedModel) onModelSubmit?.(selectedModel);
              }}
              disabled={!selectedModel?.is_compatible}
              size="md"
              variant="primary"
            >
              Select {MODEL_TYPE_LABELS_WITH_ARTICLE[modelType]}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
